import { MoreVertOutlined } from '@mui/icons-material';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { IconButtonOwnProps } from '@mui/material/IconButton';
import { isUndefined } from 'lodash';
import React from 'react';

import { globalTypes } from '@/shared/duck';

import { StyledMenuItem, StyledMenuItemText } from '../menu.styled';
import { StyledFooter, StyledHeader } from './complex-menu.styled';

interface ComplexMenuProps {
  itemList: globalTypes.MenuItemList[];
  disabled?: boolean;
  headerText?: string;
  headerSubText?: string;
  headerIconC?: React.ReactElement;
  footerItems?: globalTypes.MenuItemList[];
  iconButtonColor?: IconButtonOwnProps['color'];
  iconButtonSize?: IconButtonOwnProps['size'];
  menuIconC?: React.ReactElement;
  sx?: SxProps<Theme>;
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ComplexMenu: React.FC<ComplexMenuProps> = ({
  itemList,
  disabled,
  headerText,
  headerSubText,
  headerIconC,
  footerItems,
  iconButtonColor = 'default',
  iconButtonSize = 'medium',
  menuIconC,
  sx,
  setIsOpen,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  React.useEffect(() => {
    setIsOpen?.(open);
  }, [open, setIsOpen]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    if (disabled) {
      event.preventDefault();
    } else {
      setAnchorEl(event.currentTarget);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      {menuIconC ? (
        <Box
          display='flex'
          justifyContent='center'
          aria-label='toggle_menu'
          aria-controls={open ? 'complex-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
        >
          {menuIconC}
        </Box>
      ) : (
        <IconButton
          color={iconButtonColor}
          aria-label='toggle_menu'
          aria-controls={open ? 'complex-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          size={iconButtonSize}
        >
          <MoreVertOutlined />
        </IconButton>
      )}

      <Menu
        id='complex-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
            disablePadding: true,
          },
        }}
        sx={sx}
      >
        {(headerText || headerIconC) ? [
          <StyledHeader
            key='styles-menu-header'
            disabled
          >
            <Stack>
              {headerIconC && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {headerIconC}
                </Box>
              )}

              {headerText && (
                <Typography
                  variant={'subtitle2'}
                  fontWeight={'bold'}
                  color={theme.palette.text.primary}
                >
                  {headerText}
                </Typography>
              )}

            </Stack>

            {headerSubText && (
              <Typography
                variant={'caption'}
                color={theme.palette.text.secondary}
              >
                {headerSubText}
              </Typography>
            )}
          </StyledHeader>,
          <Divider key={'styles-menu-header-divider'} sx={{ m: 'initial !important' }} />,
        ] : null}

        {itemList.map((item, index) => {
          const iconPosition = item.titleIconPosition || 'left';

          return item.isHidden ? null : (
            <Tooltip
              key={`complex-menu-tooltip-${item.title}-${index}`}
              arrow
              placement={item.tooltipPlacement || 'top'}
              title={item.disabled ? item.tooltipText || '' : ''}
            >
              <span>
                <StyledMenuItem
                  onClick={() => {
                    item.itemAction();
                    return (
                      (
                        isUndefined(item.closeAfterAction) || item.closeAfterAction
                      ) && handleClose()
                    );
                  }}
                  disabled={item.disabled}
                >
                  <Stack flexDirection={'row'} gap={item.titleStackGap || 0.25}>
                    {item.titleIcon && iconPosition === 'left' && (
                      <ListItemIcon sx={{ alignItems: 'center' }}>
                        {item.titleIcon}
                      </ListItemIcon>
                    )}

                    <StyledMenuItemText isRedText={item.isRedText}>
                      {item.title}
                    </StyledMenuItemText>

                    {item.titleIcon && iconPosition === 'right' && (
                      <ListItemIcon sx={{ alignItems: 'center' }}>
                        {item.titleIcon}
                      </ListItemIcon>
                    )}
                  </Stack>
                </StyledMenuItem>
              </span>
            </Tooltip>
          );
        })}

        {!!itemList.length && !!footerItems?.length && <Divider />}

        {footerItems?.map((footerItem, index) => {
          if (footerItem.isHidden) {
            return null;
          }

          return (
            <Tooltip
              key={`menu-tooltip-${footerItem.title}-${index}`}
              arrow
              placement={footerItem.tooltipPlacement || 'top'}
              title={footerItem.disabled ? footerItem.tooltipText || '' : ''}
            >
              <span>
                <StyledFooter
                  key={index}
                  onClick={() => {
                    footerItem.itemAction();
                    handleClose();
                  }}
                  disabled={footerItem.disabled}
                  sx={{
                    cursor: `${footerItem.disabled ? 'default' : 'pointer'}`,
                  }}
                >

                  {footerItem.titleIcon && (
                    <ListItemIcon
                      sx={{ marginRight: '0.25rem' }}
                    >
                      {footerItem.titleIcon}
                    </ListItemIcon>
                  )}

                  <StyledMenuItemText isRedText={footerItem.isRedText}>
                    {footerItem.title}
                  </StyledMenuItemText>
                </StyledFooter>
              </span>
            </Tooltip>
          );
        })}
      </Menu>
    </>
  );
};

export default ComplexMenu;